import PrimaryPageLayout from '@/layouts/PrimaryPageLayout'
import { usePrimaryPage } from '@/PageManager'
import { parseFilter } from '@/lib/filter-parser'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import SearchInput from '@/components/SearchInput'
import { HugeiconsIcon } from '@hugeicons/react'
import { Router } from '@hugeicons/core-free-icons'
import RelaySelector, { Item, SelectableItem } from '@/components/RelaySelector'
import { Filter } from '@nostr/tools/filter'
import client from '@/services/client.service'
import { Button } from '@/components/ui/button'
import NoteCard, { NoteCardLoadingSkeleton } from '@/components/NoteCard'
import { useAsync } from 'react-use'
import { loadRelayInfo, RelayInfoDocument } from '@nostr/gadgets/relays'
import { TFeedSubRequest } from '@/types'
import { Event } from '@nostr/tools/wasm'
import { useTranslation } from 'react-i18next'
import { useAtomValue } from 'jotai'
import { unwrappedFavoriteRelaysWithSetsAtom, unwrappedSearchRelaysAtom } from '@/atoms/lists'
import { useCurrentPubkey } from '@/atoms/session'
import { feedKinds } from '@/universe'

const SearchPage = forwardRef(({ input: initialInput }: { input?: string }, _ref) => {
  const { t } = useTranslation()
  const { current, display, navigate } = usePrimaryPage()
  const pubkey = useCurrentPubkey()
  const [input, setInput] = useState(initialInput || '')
  const searchRelays = useAtomValue(unwrappedSearchRelaysAtom) ?? []
  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)
  const isActive = useMemo(() => current === 'search' && display, [current, display])
  const searchInputRef = useRef<HTMLInputElement>(null)
  const relaySelection = useRef<SelectableItem[]>()
  const [filter, setFilter] = useState<Filter | null>(null)
  const [relays, setRelays] = useState<string[]>(window.hallway.universe.noteSearchRelayUrls)
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const initialSelected = useMemo(
    () =>
      new Set(
        searchRelays.length
          ? searchRelays
          : window.hallway.universe.noteSearchRelayUrls ||
            window.hallway.universe.noteSearchRelayUrlsExtra
      ),
    [searchRelays]
  )

  useEffect(() => {
    setRelays(Array.from(initialSelected))
  }, [initialSelected])

  const relayItems = useMemo((): Item[] => {
    const items: Item[] = []
    const added: string[] = []

    const pushUrl = (url: string) => {
      if (added.includes(url)) return
      added.push(url)
      items.push({ kind: 'relay', url })
    }

    if (pubkey && searchRelays.length) {
      for (const url of searchRelays) pushUrl(url)
    }

    {
      // default search relays section
      items.push({ kind: 'separator' })
      for (const url of window.hallway.universe.noteSearchRelayUrls) pushUrl(url)
      for (const url of window.hallway.universe.noteSearchRelayUrlsExtra) pushUrl(url)
      // if nothing was added remove the separator
      if (items[items.length - 1].kind === 'separator') items.pop()
    }

    {
      // favorites section
      items.push({ kind: 'separator' })
      if (favorites) {
        for (const urlOrSet of favorites) {
          if (typeof urlOrSet === 'string') pushUrl(urlOrSet)
          else
            items.push({
              kind: 'relay-set',
              identifier: urlOrSet.pointer.identifier,
              relays: urlOrSet.items,
              name: urlOrSet.title
            })
        }
      }
      // if nothing was added remove the separator
      if (items[items.length - 1].kind === 'separator') items.pop()
    }

    {
      // big relays section
      items.push({ kind: 'separator' })
      for (const url of window.hallway.universe.bigRelayUrls) pushUrl(url)
      // if nothing was added remove the separator
      if (items[items.length - 1].kind === 'separator') items.pop()
    }

    return items
  }, [pubkey, searchRelays, favorites])

  useEffect(() => {
    if (isActive && initialInput && initialInput.trim()) {
      setInput(initialInput)
      parseFilter(initialInput).then(setFilter)
    }
    searchInputRef.current?.focus()
  }, [isActive, initialInput])

  const { value: subRequests } = useAsync(async (): Promise<TFeedSubRequest[] | undefined> => {
    if (!filter) return

    const f = { ...filter }
    if (!f.limit) f.limit = 20
    if (!f.kinds) f.kinds = feedKinds

    const urls = [...relays]
    if (f.search) {
      const sr = pubkey ? searchRelays : []
      const checkSearchSupported: Promise<RelayInfoDocument | null>[] = []
      urls.length = 0
      for (const url of relays) {
        if (sr.includes(url)) urls.push(url)
        else checkSearchSupported.push(loadRelayInfo(url))
      }

      for (const info of await Promise.all(checkSearchSupported)) {
        if (info?.supported_nips?.includes(50)) urls.push(info.url)
      }
    }

    return [{ source: 'relays' as const, urls, filter: f }]
  }, [filter, relays, pubkey, searchRelays])

  useEffect(() => {
    if (!subRequests) return

    setLoading(true)
    setEvents([])
    setError(null)

    if (relays.length === 0) {
      setError(t('No relays selected'))
      return
    }

    const subc = client.subscribeTimeline(
      subRequests,
      {},
      {
        onEvents(events, isFinal) {
          if (events.length) setEvents(events as Event[])
          if (isFinal) setLoading(false)
        },
        onNew() {},
        onClose(_url, _reason) {
          if (
            [
              'closed by caller',
              'relay connection errored',
              'relay connection closed',
              'pingpong timed out',
              'relay connection closed by us'
            ].includes(_reason)
          )
            return
          setError(`${_url}: ${_reason}`)
        }
      }
    )

    return () => subc.close()
  }, [subRequests])

  return (
    <PrimaryPageLayout
      pageName="search"
      titlebar={
        <div className="relative flex gap-1 items-center w-full">
          <SearchInput
            ref={searchInputRef}
            className="bg-surface-background shadow-inner border-none"
            placeholder={t('Search notes...')}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <RelaySelector
            selected={initialSelected}
            onChange={(sel) => {
              relaySelection.current = sel
            }}
            onClose={async () => {
              setRelays(
                relaySelection.current?.flatMap((item) =>
                  item.kind === 'relay' ? item.url : item.kind === 'relay-set' ? item.relays : []
                ) || []
              )
            }}
            items={relayItems || []}
          >
            <div className="size-10 flex items-center justify-center">
              <Button variant="ghost" size="icon" title="Search relays">
                <HugeiconsIcon icon={Router} className="text-muted-foreground" />
              </Button>
            </div>
          </RelaySelector>
        </div>
      }
      displayScrollToTopButton
    >
      <div className="min-h-screen">
        {loading && subRequests?.length && subRequests[0].source === 'relays' && (
          <div className="px-4 py-2 text-xs text-muted-foreground space-y-1">
            <div>{JSON.stringify(subRequests[0].filter)}</div>
            <div className="break-all">{subRequests[0].urls.join(', ')}</div>
          </div>
        )}

        {error && <div className="px-4 py-2 text-xs text-red-500">{error}</div>}

        {events.map((event) => (
          <NoteCard key={event.id} event={event} className="w-full" clamp />
        ))}

        {loading && events.length === 0 && (
          <>
            <NoteCardLoadingSkeleton />
            <NoteCardLoadingSkeleton />
          </>
        )}

        {!loading && !error && events.length === 0 && filter && (
          <div className="text-center text-sm text-muted-foreground mt-8">No results</div>
        )}
      </div>
    </PrimaryPageLayout>
  )

  async function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.stopPropagation()
      navigate('search', { input })
      searchInputRef.current?.blur()
    }
    if (e.key === 'Escape') {
      searchInputRef.current?.blur()
    }
  }
})

SearchPage.displayName = 'SearchPage'
export default SearchPage
